import React, { PropsWithChildren, useMemo } from 'react'
import { parseErrorMessage } from 'api'
import { createContext } from 'lib/createContext'
import { useBillRateShiftsQuery } from 'queries/billRates/useBillRateShiftsQuery'

const BILL_RATE_SHIFTS_PAGE_SIZE = 20

type BillRateShift = NonNullable<
  ReturnType<typeof useBillRateShiftsQuery>['data']
>['pages'][number]['items'][number]

type BillRateShiftsContextState = {
  state: {
    items: BillRateShift[]
    isLoading: boolean
    isError: boolean
    isFetchNextPageError: boolean
    errorMessage: string | undefined
    hasNextPage: boolean
    isFetchingNextPage: boolean
  }
  actions: {
    refetch: ReturnType<typeof useBillRateShiftsQuery>['refetch']
    loadMore: ReturnType<typeof useBillRateShiftsQuery>['fetchNextPage']
  }
}

const [Context, useBillRateShiftsContextValue] =
  createContext<BillRateShiftsContextState>({
    name: 'BillRateShiftsContext',
  })

export const BillRateShiftsProvider = ({
  billRateId,
  children,
}: PropsWithChildren<{ billRateId: number }>) => {
  const {
    data,
    isPending,
    isError,
    isFetchNextPageError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useBillRateShiftsQuery({
    billRateId,
    pageSize: BILL_RATE_SHIFTS_PAGE_SIZE,
  })

  const items = useMemo(
    () => data?.pages.flatMap((shiftPage) => shiftPage.items) ?? [],
    [data]
  )

  const value: BillRateShiftsContextState = {
    state: {
      items,
      isLoading: isPending,
      // Only replace the table on a first-load error, not a failed extra page.
      isError: isError && data === undefined,
      isFetchNextPageError,
      errorMessage: parseErrorMessage(error),
      hasNextPage: Boolean(hasNextPage),
      isFetchingNextPage,
    },
    actions: { refetch, loadMore: fetchNextPage },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useBillRateShiftsContextValue }
